import {
  CalendarRangeIcon,
  CompassIcon,
  DropletsIcon,
  FlameIcon,
  GemIcon,
  GlobeIcon,
  HourglassIcon,
  MountainSnowIcon,
  SearchIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de Geología y Ciencias de la Tierra',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la geología puede ser el más transformador o el más aburrido. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Qué es la geología y por qué importa',
      'Origen y edad de la Tierra',
      'Sistema Tierra: estructura interna y capas',
      'Ciclos geológicos y procesos dinámicos',
      'Geosfera, atmósfera, hidrosfera, biosfera y criósfera',
      'Métodos de estudio y herramientas geológicas',
      'Escalas de tiempo geológico',
      'Material explicado de forma clara con ejemplos locales',
    ],
    result:
      'Tus alumnos entienden la geología como una ciencia práctica y urgente, no como un conjunto de términos imposibles de memorizar.',
    icon: GlobeIcon,
  },
  {
    number: '2',
    title: 'Minerales, Rocas y Ciclo Geológico',
    subtitle: 'Todo lo que necesitan saber sobre los materiales de la Tierra',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan cómo se forman los minerales y las rocas.',
    items: [
      'Propiedades físicas y químicas de los minerales',
      'Identificación de minerales en el aula',
      'Sistemas cristalinos y estructura cristalina',
      'Rocas ígneas: formación, tipos y características',
      'Rocas sedimentarias: formación, ambientes y tipos',
      'Rocas metamórficas: procesos y transformaciones',
      'El ciclo de las rocas y sus procesos',
      'Magmatismo y cristalización',
      '15 ejemplos de identificación resueltos paso a paso',
    ],
    result: 'Tus alumnos aprenden a identificar minerales y rocas con método y confianza.',
    icon: GemIcon,
  },
  {
    number: '3',
    title: 'Tectónica de Placas, Estructura Interna y Deformación',
    subtitle: 'Cómo enseñar la dinámica de la Tierra de forma clara y práctica',
    classroom:
      'La tectónica de placas es fascinante pero compleja. Este tomo te da todo lo que necesitas para que tus alumnos comprendan cómo se mueve la Tierra.',
    items: [
      'Estructura interna de la Tierra: corteza, manto y núcleo',
      'Teoría de la tectónica de placas',
      'Tipos de placas tectónicas y límites',
      'Divergencia: expansión oceánica y rift continental',
      'Convergencia: subducción y colisión',
      'Fallas de desgarre y movimiento lateral',
      'Deformación frágil y dúctil',
      'Fallas, pliegues y otras estructuras',
      '8 casos prácticos simulados listos para el aula',
      'Ejercicios sobre identificación de estructuras',
    ],
    result:
      'Tus alumnos comprenden por qué la Tierra está en constante movimiento y cómo eso crea montañas, océanos y continentes.',
    icon: CompassIcon,
  },
  {
    number: '4',
    title: 'Sismos, Volcanismo y Riesgos Geológicos',
    subtitle: 'Peligros geológicos explicados de forma integral',
    classroom:
      'Los sismos y volcanes son complejos pero fundamentales. Este tomo te enseña a desglosarlos sin perder claridad.',
    items: [
      'Origen de los terremotos y mecanismos sísmicos',
      'Ondas sísmicas: P, S y superficiales',
      'Magnitud e intensidad de los terremotos',
      'Localización epicentral e hipocentral',
      'Predicción y pronóstico sísmico',
      'Volcanismo: procesos y tipos de erupciones',
      'Productos volcánicos: lava, ceniza y gases',
      'Peligros volcánicos y zonas de riesgo',
      'Vulnerabilidad y exposición sísmica',
      'Gestión del riesgo geológico',
      'Casos de sismos y volcanes en Latinoamérica',
      '10 casos de aplicación práctica',
    ],
    result:
      'Tus alumnos entienden por qué ocurren los terremotos y volcanes, y cómo las comunidades pueden prepararse.',
    icon: FlameIcon,
  },
  {
    number: '5',
    title: 'Geomorfología, Meteorización, Erosión y Sedimentación',
    subtitle: 'Cómo se forman y transforman los paisajes',
    classroom:
      'Los procesos que crean el relieve son fascinantes. Este tomo te da las herramientas para explicarlos con claridad.',
    items: [
      'Meteorización: mecánica, química y biológica',
      'Erosión y transporte de sedimentos',
      'Procesos fluviales: ríos, valles y deltas',
      'Procesos costeros: olas, mareas y acantilados',
      'Procesos eólicos: dunas y erosión por viento',
      'Procesos glaciares: glaciación y formas resultantes',
      'Sedimentación y ambientes depositacionales',
      'Formación de suelos y horizontes edáficos',
      'Paisajes y geoformas características',
      'Cambios en el relieve a diferentes escalas',
      'Análisis de casos en Latinoamérica',
      '10 casos de análisis geomorfológico',
    ],
    result: 'Tus alumnos entienden por qué existen montañas, ríos, playas y desiertos, y cómo cambian con el tiempo.',
    icon: MountainSnowIcon,
  },
  {
    number: '6',
    title: 'Estratigrafía, Fósiles y Tiempo Geológico',
    subtitle: 'Cómo leer la historia de la Tierra en las rocas',
    classroom:
      'La estratigrafía y los fósiles son la llave para entender el pasado de la Tierra. Este tomo cierra la visión integral.',
    items: [
      'Estratos y principios estratigráficos',
      'Correlación de capas y secuencias',
      'Discontinuidades y brechas en el registro',
      'Qué son los fósiles y cómo se forman',
      'Fosilización: permineralización, moldeo y vaciado',
      'Tipos de fósiles: cuerpo, icnofósiles y trazas',
      'Clasificación de fósiles',
      'Datación relativa: métodos y principios',
      'Datación absoluta: radiométrica e isotópica',
      'Escala del tiempo geológico',
      'Eras, períodos y épocas geológicas',
      'Extinciones masivas y evolución de la vida',
      'Reconstrucción de ambientes antiguos',
      '8 casos de análisis estratigráfico y paleontológico',
    ],
    result: 'Tus alumnos aprenden a interpretar la historia de la Tierra y cómo ha evolucionado la vida.',
    icon: HourglassIcon,
  },
  {
    number: '7',
    title: 'Agua Subterránea, Recursos Geológicos y Ambiente',
    subtitle: 'Geología aplicada: recursos, sostenibilidad y desafíos',
    classroom:
      'Este es el corazón práctico. Aquí tus alumnos aprenden cómo la geología afecta la vida cotidiana.',
    items: [
      'Hidrogeología: acuíferos y agua subterránea',
      'Porosidad, permeabilidad e infiltración',
      'Recarga y descarga de acuíferos',
      'Contaminación del agua subterránea',
      'Recursos minerales y su importancia',
      'Minerales metálicos y no metálicos',
      'Depósitos minerales y yacimientos',
      'Recursos energéticos: petróleo, gas y carbón',
      'Energías renovables: geotermia y minería',
      'Impacto ambiental de la extracción',
      'Gestión sostenible de recursos',
      'Relaciones entre geología y ambiente',
      '12 casos prácticos de recursos y ambiente',
    ],
    result: 'Tus alumnos comprenden cómo la geología sustenta la civilización y los desafíos de la sostenibilidad.',
    icon: DropletsIcon,
  },
  {
    number: '8',
    title: 'Casos Prácticos y Desafíos de Geología',
    subtitle: 'Problemas reales para analizar y resolver',
    classroom: 'Este tomo convierte la teoría en práctica. Aquí tus alumnos aplican conceptos a situaciones reales.',
    items: [
      'Análisis de evidencias geológicas',
      'Interpretación de mapas y perfiles geológicos',
      'Formulación de hipótesis geológicas',
      'Resolución de problemas geológicos',
      'Casos de terremotos destructivos',
      'Casos de erupciones volcánicas significativas',
      'Casos de deslizamientos y aludes',
      'Casos de inundaciones y drenaje',
      'Casos de contaminación ambiental',
      'Casos de cambio climático y geología',
      'Desafíos de recursos hídricos',
      'Desafíos de gestión de residuos',
      'Integración de conceptos en escenarios complejos',
      '20 casos prácticos resueltos paso a paso',
    ],
    result: 'Tus alumnos salen preparados para pensar como geólogos y resolver problemas del mundo real.',
    icon: SearchIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de la Geología — Planificación y Proyectos 2026',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Enfoques actuales para enseñar geología',
      'Organización progresiva de contenidos',
      'Planificación anual orientativa',
      'Secuencias didácticas completas',
      'Prácticas de aula y laboratorio estructuradas',
      'Identificación de minerales en el laboratorio',
      'Análisis de muestras de roca',
      'Trabajos de campo e interpretación',
      'Aprendizaje basado en proyectos',
      'Integración de herramientas digitales y SIG',
      'Trabajo con recursos limitados',
      'Evaluación teórica y práctica',
      'Rúbricas y criterios de observación',
      'Salidas de campo y seguridad',
      '10 proyectos integradores listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: CalendarRangeIcon,
  },
];
